import { applySeo, heroLines } from "../lib/seo";
import { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Clock, Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT, BRAND_LOGOS, LOCATIONS, SHOWROOM_PAGE as SP } from "../data/site";
import { MaskedLines, Reveal, SectionHeading } from "../components/site/Reveal";
import { CTABand } from "../components/site/CTABand";

export default function Showroom() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);

  useEffect(() => {
    applySeo({ paths: ["/showroom/"], fallbackTitle: "Our Showrooms | Cedar Views" });
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="showroom-page">
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden flex items-end">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={SP.heroImage} alt="Inside the Cedar Views flagship showroom at Fairoaks Airport, Chobham" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-[#050A15]/45 to-[#050A15]/25" />
        </motion.div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 w-full">
          <Reveal delay={0.1}><p className="overline-x mb-5">{SP.heroOverline}</p></Reveal>
          <MaskedLines as="h1" lines={heroLines(["Our Showrooms"], "/showroom/")} delay={0.2} className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-7xl text-white" />
          <Reveal delay={0.5} className="mt-6">
            <h2 className="text-base md:text-lg text-white/70 max-w-xl font-normal">
              {SP.heroSub}
            </h2>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <SectionHeading number="01" kicker={SP.introKicker} title={SP.introTitle} />
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-6">
          {SP.intro.map((para, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className="text-white/65 leading-relaxed text-base md:text-lg">{para}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TWO LOCATIONS */}
      <section className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeading number="02" kicker="Where to find us" title={SP.locationsTitle} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {LOCATIONS.map((loc, i) => (
              <Reveal key={loc.id} delay={i * 0.12} className="group border hairline bg-[#0B162C]">
                <div data-testid={`location-card-${loc.id}`}>
                  <div className="img-hover aspect-[16/10] overflow-hidden">
                    <img src={loc.img} alt={`${loc.name}, ${loc.place}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-8 lg:p-10">
                    <p className="overline-x mb-3">{loc.id === "longacres" ? "New · Now open" : "Flagship"}</p>
                    <h3 className="font-display uppercase tracking-tight text-2xl text-white">{loc.name}</h3>
                    <p className="text-white/50 text-sm mt-1">{loc.place}</p>
                    <p className="text-white/65 leading-relaxed mt-5">{loc.desc}</p>
                    <div className="flex flex-wrap gap-x-10 gap-y-4 mt-7 text-sm">
                      <span className="flex items-start gap-2.5 text-white/60"><MapPin size={15} className="mt-0.5 text-white/35" />{(loc.address || []).join(", ")}</span>
                      <span className="flex items-center gap-2.5 text-white/60"><Clock size={15} className="text-white/35" />{loc.hours}</span>
                    </div>
                    <Link to="/contact/" data-testid={`location-book-${loc.id}`} className="btn-ghost mt-8">
                      Book an appointment <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y hairline bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {[
            { icon: MapPin, t: "Find us", d: `${CONTACT.address.join(", ")}, a short 20-minute drive from Oxshott, Weybridge and surrounding towns.` },
            { icon: Clock, t: "Opening hours", d: CONTACT.hours },
            { icon: Phone, t: "Book a visit", d: `Call ${CONTACT.phone} or email ${CONTACT.email} to arrange a consultation.` },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1} className="bg-[#070E1D] p-10">
              <c.icon size={20} className="text-white/40" />
              <h3 className="font-display font-medium text-xl text-white mt-6 mb-3">{c.t}</h3>
              <p className="text-sm text-white/55 leading-relaxed">{c.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <SectionHeading number="03" kicker="Inside the showroom" title={SP.galleryTitle} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SP.galleryLarge.map((src, i) => (
            <Reveal key={src + i} delay={i * 0.12} className="img-hover aspect-[4/3]">
              <img src={src} alt={`Inside the Cedar Views showroom ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </Reveal>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {SP.galleryGrid.map((src, i) => (
            <Reveal key={src + i} delay={(i % 4) * 0.06} className="img-hover">
              <img src={src} alt={`Showroom display ${i + 1}`} loading="lazy" className="w-full h-full object-cover aspect-square" />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t hairline bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeading number="04" kicker="On display" title={SP.logosTitle} light />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/5">
          {BRAND_LOGOS.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.06} className="bg-white px-8 py-12 lg:py-16 flex items-center justify-center">
              <img src={b.src} alt={`${b.name} logo`} loading="lazy" className="h-14 lg:h-20 max-w-[70%] w-auto object-contain" />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14">
          <Link to="/contact/" data-testid="showroom-contact-link" className="btn-dark">
            Arrange a showroom visit <ArrowRight size={14} />
          </Link>
        </Reveal>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
